import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/shared/utils/Numbers.tsx";
import useDateFormatter from "@/shared/custom-hook/useDateFormatter.ts";
import { DateStyle } from "@/shared/utils/Dates.ts";
import type { LaborCostEmployee } from "@/pages/human-resources/time-management/business/analysis/LaborCostEmployee.ts";
import "./LaborCostDetailsEmpRow.scss";
import useScheduleReferenceData from "@/pages/human-resources/time-management/schedules/reference-provider/useScheduleReferenceData.ts";
import useFormatDurationMinutes from "@/pages/human-resources/time-management/model/useFormatDurationMinutes.ts";
import useScheduleUserPermissions from "@/pages/human-resources/time-management/hooks/useScheduleUserPermissions.ts";

interface Props {
  emp: LaborCostEmployee;
  open: boolean;
}

export function LaborCostDetailsEmpRow({ emp, open }: Props): ReactElement {
  const { t } = useTranslation("common");
  const {
    domainData: { domainId },
  } = useScheduleReferenceData();
  const { formatDate } = useDateFormatter();
  const formatDuration = useFormatDurationMinutes();
  const { canSeeEmployeeContracts, canSeeEmployeePersonalData } =
    useScheduleUserPermissions();
  const employeeUrl = `/content/employe/?task=edit&idDomain=${domainId}&idEmploye=${emp.employeeId}`;
  const shouldLink = canSeeEmployeePersonalData && canSeeEmployeeContracts;

  return (
    <>
      <tr
        key={emp.employeeId}
        className={open ? "employee-row" : "employee-row hidden-row"}
      >
        <td />
        <td className="cell-employee-name">
          {shouldLink && (
            <a
              href={employeeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="employee-link"
            >
              {emp.employeeName}
            </a>
          )}
          {!shouldLink && emp.employeeName}
        </td>
        <td className="cell-charge-rate">
          {formatNumber(t, emp.chargeRate, 0)} %
        </td>
        <td className="cell-contract-start">
          {formatDate(emp.contractStart, DateStyle.DATE)}
        </td>
        <td className="cell-hours">{formatDuration(emp.workMinutes)}</td>
        <td className="cell-hourly-rate">
          {formatNumber(t, emp.hourlyRate, 2) + " €"}
        </td>
        <td className="cell-contract-cost">
          {formatNumber(t, emp.contractCost, 2)} €
        </td>
        <td className="cell-bonuses">{formatNumber(t, emp.bonuses, 2)} €</td>
        <td className="cell-total-cost">
          {formatNumber(t, emp.totalCost, 2)} €
        </td>
      </tr>
    </>
  );
}
